import React from 'react';
import cx from 'classnames';
import { OptionsType } from 'embla-carousel/components/Options';
import useEmblaCarousel from 'embla-carousel-react';

import Spaced from 'components/Spaced';
import useKeyDown from 'hooks/useKeyDown';
import placeholderImage from 'images/1920x1280.png';
import arrowLeft from 'images/icon_arrow_left@2x.png';
import arrowRight from 'images/icon_arrow_right@2x.png';
import styles from './Carousel.module.css';

export const Slide: React.FCWithChildren<{
  src: string;
  srcSet?: string;
  inView: boolean;
  className?: string;
}> = ({ src, srcSet, inView, className }) => {
  const [hasLoaded, setHasLoaded] = React.useState(false);

  const setLoaded = React.useCallback(() => {
    if (inView) setHasLoaded(true);
  }, [inView, setHasLoaded]);

  return (
    <img
      alt=""
      className={cx(styles.carouselImage, className, hasLoaded ? 'hasLoaded' : '')}
      src={inView ? src : placeholderImage.src}
      srcSet={srcSet}
      onLoad={setLoaded}
    />
  );
};

export interface CarouselRenderProps {
  scrollNext: () => void;
  scrollPrev: () => void;
  scrollTo: (index: number) => void;
  selectedIndex: number;
  slidesInView: number[];
}

export interface CarouselProps {
  hideControls?: boolean;
  controls?: React.ReactNode | ((ctx: CarouselRenderProps) => React.ReactNode);
  options?: Partial<OptionsType>;
  enableKeyboardNavigation?: boolean;
  children: React.ReactNode | ((ctx: CarouselRenderProps) => React.ReactNode);
}

const Carousel = ({
  children,
  hideControls = false,
  controls,
  options = {
    containScroll: 'keepSnaps',
  },
  enableKeyboardNavigation = false,
}: CarouselProps) => {
  const { startIndex: initialStartIndex = 0 } = options;
  const [carouselRef, carousel] = useEmblaCarousel(options);
  const [thumbRef, thumbCarousel] = useEmblaCarousel(options);

  const [selectedIndex, setSelectedIndex] = React.useState(initialStartIndex);
  const [slidesInView, setSlidesInView] = React.useState<number[]>([]);

  const scrollPrev = React.useCallback(() => carousel?.scrollPrev(), [carousel]);
  const scrollNext = React.useCallback(() => carousel?.scrollNext(), [carousel]);

  const findSlidesInView = React.useCallback(() => {
    if (!carousel) return;

    setSlidesInView(slidesInView => {
      if (slidesInView.length === carousel.slideNodes().length) {
        carousel.off('select', findSlidesInView);
      }
      const inView = carousel
        .slidesInView(true)
        .filter((index: number) => slidesInView.indexOf(index) === -1);

      return slidesInView.concat(inView);
    });
  }, [carousel, setSlidesInView]);

  const scrollTo = React.useCallback(
    (index: number) => {
      if (!carousel || !thumbCarousel) return;

      if (thumbCarousel.clickAllowed()) {
        carousel.scrollTo(index);
      }
    },
    [carousel, thumbCarousel],
  );

  const onSelect = React.useCallback(() => {
    if (!carousel) return;

    const newIndex = carousel.selectedScrollSnap();
    setSelectedIndex(newIndex);
  }, [carousel, setSelectedIndex]);

  React.useEffect(() => {
    if (!carousel) return;

    carousel.on('select', onSelect);
    carousel.on('select', findSlidesInView);
  }, [carousel, onSelect, findSlidesInView]);

  React.useEffect(() => {
    if (!carousel) return;
    findSlidesInView();
    onSelect();
  }, [findSlidesInView, onSelect, carousel]);

  React.useEffect(() => {
    if (!carousel || !thumbCarousel) return;

    thumbCarousel.scrollTo(selectedIndex);
  }, [thumbCarousel, carousel, selectedIndex]);

  useKeyDown(e => {
    if (enableKeyboardNavigation) {
      if (e.key === 'ArrowRight') {
        carousel?.scrollNext();
      }
      if (e.key === 'ArrowLeft') {
        carousel?.scrollPrev();
      }
    }
  });

  const ctx: CarouselRenderProps = {
    scrollNext,
    scrollPrev,
    scrollTo,
    selectedIndex,
    slidesInView,
  };

  const childrenRendered = typeof children === 'function' ? children(ctx) : children;

  return (
    <>
      {typeof controls === 'function' ? controls(ctx) : controls}

      <Spaced className={styles.fullWidth} spacing={2}>
        <div className={styles.carousel}>
          <div ref={carouselRef} className={styles.viewport}>
            <div className={styles.container}>{childrenRendered}</div>
          </div>

          {!hideControls && (
            <>
              <button className={cx(styles.paginate, styles.paginatePrev)} onClick={scrollPrev}>
                <img alt="Paginate back" src={arrowLeft.src} />
              </button>
              <button className={cx(styles.paginate, styles.paginateNext)} onClick={scrollNext}>
                <img alt="Paginate forward" src={arrowRight.src} />
              </button>
            </>
          )}

          {!hideControls && (
            <div ref={thumbRef} className={styles.indicators}>
              {Array.isArray(childrenRendered) &&
                childrenRendered.length > 1 &&
                childrenRendered.map((_, index) => (
                  <button
                    onClick={() => scrollTo(index)}
                    key={index}
                    // eslint-disable-next-line css-modules/no-undef-class
                    className={cx(styles.indicator, { [styles.active]: index === selectedIndex })}
                  />
                ))}
            </div>
          )}
        </div>
      </Spaced>
    </>
  );
};

export default Carousel;
